import {useEffect, useRef, useState} from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {Box, Button, Divider, Drawer, Grid, Typography} from "@mui/material";
import {Account} from "../Account/Account";
import {flexStyles} from "../../../component/theme/theme";
import {useForm} from "react-hook-form";
import Snackbar from "@mui/material/Snackbar";
import {Link, useLocation} from "react-router";
import {StyledButtonContained, StyledButtonOutlined, WizardStepConnector, WizardStepIcon} from "../../../utils/uiUtils";
import {createCustomer, createCustomerUser, fetchCustomer, fetchCustomers, fetchCustomerUsers, fetchJob, patchCustomer, patchUser} from "../../../utils/apiCalls";
import {fetchCompanyList} from "../../../utils/dashboardCalls";
import {describeInvitation} from "../../../utils/invitationMessage";
import {apiErrorReason} from "../../../utils/apiErrorReason";
import {JobWizard} from "../JobCreation/JobWizard";

const steps = ["Account", "Job", "Shipping", "Tape", "Ingestion", "Fee", "Documents"];

const toTwoFAValue = (v) =>
    v == "verify_by_sms" ? "sms" :
    v == "verify_by_authenticator" ? "authenticator" : "email";

export const AccountCreation = (props) => {
    const [twoFAMode, setTwoFAMode] = useState("verify_by_email");
    const [customerId, setCustomerId] = useState(null);
    const [selectedImage, setSelectedImage] = useState([{url: "", base64URL: "", file: ""}]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [activeStep, setActiveStep] = useState(0);
    const [saasCompanies, setSaasCompanies] = useState([]);
    const [usedArkbridgeIds, setUsedArkbridgeIds] = useState([]);
    const [existingAddresses, setExistingAddresses] = useState([]);
    const [existingUsers, setExistingUsers] = useState([]);
    // Populated by Account component. Calling it flushes UserBadge and AddressBadge local state upstream
    // before the form submits, so the operator doesn't need to press Enter inside the badge, and returns
    // false when the Primary Contact is incomplete — see the comment on contactGateRef in Account.jsx.
    const contactGateRef = useRef(null);

    const location = useLocation();
    const isEditClient = location?.state?.isEditClient;

    const openSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const form = useForm({
        defaultValues: {
            shippingLinks: [{link: ""}],
            tape: {tapeDetails: [{tapeType: "", number: 0, tapeFormat: ""}]},
        },
    });
    const {register, handleSubmit, setValue, getValues, watch, reset} = form;

    useEffect(() => {
        loadStepData();
        loadCompanyData();
    }, []);

    const loadCompanyData = () => {
        // Fetch Django customers to know which arkbridge IDs are already used
        fetchCustomers()
            .then(customers => {
                setUsedArkbridgeIds(
                    customers
                        .map(c => parseInt(c.arkbridge_customer_id, 10))
                        .filter(id => !isNaN(id) && id > 0)
                );
                setExistingAddresses(
                    customers
                        .filter(c => c.contact_address?.address_line_1)
                        .map(c => ({...c.contact_address, _label: c.company_name}))
                );
                return fetchCompanyList();
            })
            .then(companyData => {
                if (companyData?.result?.data) {
                    setSaasCompanies(companyData.result.data);
                }
            })
            .catch(error => console.log(error));
    };

    // Only called for step 0 (Account) — OrderWizard handles data loading for steps 1-6
    const loadStepData = () => {
        const _orderUUID = window?.location?.href?.split("uuid=")[1];

        // Shared helper: loads customer fields + user fields into the form
        const loadCustomerData = (cId) => {
            fetchCustomer(cId)
                .then((customer) => {
                    setCustomerId(customer.uuid);
                    setValue("companyName", customer.company_name);
                    setValue("arkbridge_customer_id", customer.arkbridge_customer_id);
                    setValue("email", customer.email);
                    setValue("WorkLocation", customer.work_location);
                    setValue("StorageOption", customer.storage_option);
                    setValue("postal_code", customer.contact_address.postal_code);
                    setValue("State", customer.contact_address.state);
                    setValue("Country", customer.contact_address.country);
                    setValue("City", customer.contact_address.city);
                    setValue("phone_number", customer.phone_number);
                    setValue("mobile_number", customer.mobile_number);
                    setValue("address_line_1", customer.contact_address.address_line_1);
                    setValue("address_line_2", customer.contact_address.address_line_2);
                    selectedImage.url = customer.company_logo_url;
                    return fetchCustomerUsers(customer.uuid);
                })
                .then((users) => {
                    setExistingUsers(users.results || []);
                    const user = users.results[0];
                    setValue("title",        user.title);
                    setValue("first_name",   user.first_name);
                    setValue("middle_name",  user.middle_name);
                    setValue("last_name",    user.last_name);
                    setValue("email",        user.email);
                    setValue("phone_number", user.phone_number);
                    // Mobile now gets the same treatment as phone above: the contact's own number wins over the
                    // customer's, since the contact is who this half of the form is about. Only when they have
                    // one, though — every user saved before mobile_number reached the API has none, and blanking
                    // the box would make the operator retype a number the customer record still holds.
                    if (user.mobile_number) setValue("mobile_number", user.mobile_number);
                    setValue("entra_login",  user.entra_login === true);
                    setValue("isAccountStepLoading", false);
                    if (user.two_step_verification) {
                        if (user.two_step_verification == "sms") {
                            setValue("verify_by_sms", true);
                            setTwoFAMode("verify_by_sms");
                        } else if (user.two_step_verification == "authenticator") {
                            setValue("verify_by_authenticator", true);
                            setTwoFAMode("verify_by_authenticator");
                        } else {
                            setValue("verify_by_email", true);
                            setTwoFAMode("verify_by_email");
                        }
                    }
                })
                .catch((err) => {
                    console.log(err);
                    setValue("isAccountStepLoading", false);
                });
        };

        if (isEditClient) {
            setValue("isAccountStepLoading", true);
            loadCustomerData(location?.state?.customerId);
        } else if (_orderUUID) {
            fetchJob(_orderUUID)
                .then((order) => {
                    setValue("isAccountStepLoading", true);
                    loadCustomerData(order.customer);
                })
                .catch((error) => {
                    console.log(error);
                    setValue("isAccountStepLoading", false);
                });
        }
    };

    const accountCreation = (data, isNext) => {
        if (!data.companyName) {
            openSnackbar("Please select a company name.", "error");
            return;
        }
        const payLoadCustomer = {
            contact_address: {
                address_line_1: data.address_line_1,
                address_line_2: data.address_line_2,
                postal_code: data.postal_code,
                country: data.Country,
                state: data.State,
                city: data.City,
            },
            email: data.email,
            company_name: data.companyName,
            arkbridge_customer_id: data.arkbridge_customer_id,
            work_location: data.WorkLocation,
            phone_number: data.phone_number,
            mobile_number: data.mobile_number,
            storage_option: data.StorageOption,
            company_logo: selectedImage?.base64URL?.split(",")[1],
            domain: data?.length ? data.domain.map((e) => e.name) : [],
            mfa_sms: data.two_step_verification == "verify_by_sms",
            mfa_email: data.two_step_verification == "verify_by_email",
            mfa_authenticator: data.two_step_verification == "verify_by_authenticator",
            two_step_verification: toTwoFAValue(data.two_step_verification),
        };
        const _orderUUID = window?.location?.href?.split("uuid=")[1];

        if (_orderUUID || customerId || isEditClient) {
            const resolvedCustomerId = isEditClient ? location?.state?.customerId : customerId;
            setValue("tenant_id", resolvedCustomerId);
            setCustomerId(resolvedCustomerId);

            patchCustomer(resolvedCustomerId, payLoadCustomer)
                .then((customer) => {
                    if (customer) {
                        accountRegistration(data, customer);
                        openSnackbar("Data added successfully.");
                        setValue("tenant_id", customer.uuid);
                        setCustomerId(customer.uuid);
                        if (!isEditClient) {
                            isNext && handleNext();
                        }
                    }
                })
                .catch((error) => {
                    setValue("tenant_id", "0");
                    setCustomerId(null);
                    console.log(error);
                    openSnackbar(customerNotSaved(error), "error");
                });
        } else {
            createCustomer(payLoadCustomer)
                .then((customer) => {
                    if (customer) {
                        setValue("tenant_id", customer.uuid);
                        setCustomerId(customer.uuid);
                        accountRegistration(data, customer);
                        openSnackbar("Data added successfully.");
                        setTimeout(() => {
                            isNext && handleNext();
                        }, 0);
                    }
                })
                .catch((error) => {
                    console.log(error);
                    openSnackbar(customerNotSaved(error), "error");
                });
        }
    };

    // The customer save used to fail into the console, leaving only the interceptor's banner, which gives the
    // API's message without the field it is about.
    const customerNotSaved = (error) => {
        const reason = apiErrorReason(error);
        return reason ? `The customer was not saved: ${reason}` : "The customer was not saved. Check the details and try again.";
    };

    const accountRegistration = (data, customer) => {
        const payload = {
            title: data.title,
            first_name: data.first_name,
            middle_name: data.middle_name,
            last_name: data.last_name,
            email: data.email,
            role: "owner",
            job_type: "permanent",
            phone_number: data.phone_number,
            // Mobile is the number this page insists on, so it has to reach the contact's own User row and
            // not just Customer.mobile_number. It was absent here, which meant the one mandatory number on
            // the Account step was the one number the primary contact did not have on file.
            mobile_number: data.mobile_number,
            customer: customer.uuid,
            two_step_verification: toTwoFAValue(data.two_step_verification),
            // The Sign-in Method beside Last Name. Sent every time, as a boolean: the API's default is password,
            // so leaving it out would quietly undo a Microsoft choice, and the hidden input it is registered on
            // can hand back the string "true".
            entra_login: data.entra_login === true || data.entra_login === "true",
        };
        upsertCustomerUser(payload);
    };

    /*
     * Saves the Primary Contact, as a second request after the customer itself.
     *
     * It can fail on its own, and it used to fail into the console: "Data added successfully." stayed on
     * screen from the customer save, the wizard moved on to Job, and the operator's only clue was the
     * generic error toast the interceptor raises for any 400 — which does not name the field it came from.
     * What they were left with was a customer with no contact on it and nothing saying so.
     */
    const upsertCustomerUser = (payload) => {
        const contactNotSaved = (error) => {
            console.log(error);
            const reason = apiErrorReason(error);
            openSnackbar(
                reason
                    ? `The customer was saved, but its primary contact was not: ${reason}`
                    : "The customer was saved, but its primary contact was not. Go back to Account, check the contact, and save again.",
                "error"
            );
        };

        fetchCustomerUsers(payload.customer)
            .then((users) => (
                users?.results?.length > 0
                    ? patchUser(payload.customer, users.results[0].uuid, payload)
                    // Only a new contact is sent a sign-in email, so only this answer carries an `invitation`;
                    // a patch falls through describeInvitation to the plain message.
                    : createCustomerUser(payload.customer, payload)
            ))
            .then((saved) => openSnackbar(describeInvitation(saved, {
                saved: "Primary contact saved",
                fallback: "Data added successfully.",
            })))
            .catch(contactNotSaved);
    };

    const handleNext = () => setActiveStep(1);

    const handleFormData = (data, event) => {
        const isNext = event?.nativeEvent?.submitter?.value === "saveAndContinue";
        accountCreation(data, isNext);
    };

    return (
        <>
            <Snackbar
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                open={snackbarOpen}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                ContentProps={{
                    sx: snackbarSeverity === "error"
                        ? {backgroundColor: '#FF6C65', color: '#FFFFFF'}
                        : {}
                }}
            />
            <Grid
                sx={{visibility: isEditClient ? "visible" : "hidden"}}
                container
                spacing={2}
                paddingBottom={4}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: "12px"}}>
                    <img src="/assets/img/cart.svg" alt=""/>
                    <img src="/assets/img/arrow_left.svg" alt=""/>
                    <Link to="/Customers" style={{textDecoration: "none"}}>
                        {" "}
                        <small className="grey-color">Customers</small>
                    </Link>
                    <img src="/assets/img/arrow_left.svg" alt=""/>
                    <small className="grey-color">{location?.state?.customerName}</small>
                </Box>
                <Grid item xs={12}>
                    <Divider
                        sx={{background: "#EAEAEA", width: "100%"}}
                        orientation="horizontal"
                    />
                </Grid>
            </Grid>
            <Grid
                container
                spacing={2}
                paddingBottom={6}
                sx={{paddingX: {xs: 0.5, md: 4}}}
            >
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    padding={2}
                >
                    <Grid
                        container
                        direction={"column"}
                        style={activeStep === 0 ? {border: "1px solid #C7CCD0", borderRadius: "8px"} : {}}
                    >
                        {/* Stepper */}
                        <Grid item>
                            <Box
                                sx={{
                                    height: "80px",
                                    borderRadius: "8px 8px 0px 0px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    boxShadow: "0px 2px 6px 0px #00000040",
                                    backgroundColor: "#FFC540",
                                    justifyItems: "center",
                                }}
                            >
                                <Stepper
                                    sx={{visibility: isEditClient ? "hidden" : "visible"}}
                                    alternativeLabel
                                    activeStep={activeStep}
                                    connector={<WizardStepConnector/>}
                                >
                                    {steps.map((step, index) => (
                                        <Step key={step}>
                                            <StepLabel StepIconComponent={WizardStepIcon}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "Poppins",
                                                        color: "#3A3E3F",
                                                        fontSize: "14px",
                                                        fontWeight: activeStep === index ? 700 : 400,
                                                        lineHeight: "14px",
                                                        letterSpacing: "0em",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {step}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                        </Grid>

                        {/* Step 0: Account */}
                        {activeStep === 0 && watch("isAccountStepLoading") !== true && (
                            <form
                                id="mainForm"
                                /*
                                 * The browser is not the one to raise a missing field here. Company Name and
                                 * Work Location carry `required`, and it was the browser that answered for
                                 * them, with its own "Please fill out this field." bubble floating over the
                                 * boxes below — nothing like the gold line every other field on this page
                                 * uses, and it stopped the submit handler running at all, so the badges never
                                 * got to say what was wrong with them either. The attributes stay for the star
                                 * and for aria-required; the gate in Account.jsx does the asking.
                                 */
                                noValidate
                                onSubmit={(e) => {
                                    /*
                                     * A contact that fails its own validation does not reach the API. The
                                     * badge has already said which field is wrong and re-expanded itself
                                     * to show it; going ahead would trade that for a 400 arriving after
                                     * the Save, naming a field the reader can no longer see.
                                     *
                                     * preventDefault only on this branch — handleSubmit calls it itself
                                     * on the way through, and skipping it here would let the browser
                                     * submit the form natively and navigate away.
                                     */
                                    if (contactGateRef.current?.() === false) {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleSubmit(handleFormData)(e);
                                }}
                                style={{width: "100%"}}
                            >
                                <Grid item xs={12}>
                                    <Account
                                        register={register}
                                        setValue={setValue}
                                        getValues={getValues}
                                        watch={watch}
                                        selectedImage={selectedImage}
                                        setSelectedImage={setSelectedImage}
                                        twoFAMode={twoFAMode}
                                        setTwoFAMode={setTwoFAMode}
                                        saasCompanies={saasCompanies}
                                        usedArkbridgeIds={usedArkbridgeIds}
                                        existingAddresses={existingAddresses}
                                        existingUsers={existingUsers}
                                        onSave={handleSubmit(handleFormData)}
                                        contactGateRef={contactGateRef}
                                    />
                                </Grid>
                            </form>
                        )}

                        {/* Steps 1-6: JobWizard */}
                        {activeStep >= 1 && (
                            <JobWizard
                                externalForm={form}
                                externalActiveStep={activeStep - 1}
                                onActiveStepChange={(s) => {
                                    if (s === -1) {
                                        setActiveStep(0);
                                        loadStepData();
                                    } else {
                                        setActiveStep(s + 1);
                                    }
                                }}
                                customerId={customerId}
                                showStepper={false}
                            />
                        )}
                    </Grid>

                    {/* Step 0 button bar */}
                    {activeStep === 0 && (
                        <Drawer
                            variant={"persistent"}
                            anchor={"bottom"}
                            open
                            sx={{
                                "& .MuiDrawer-paper": {
                                    backgroundColor: "#2C2C2C",
                                    boxShadow: "0px -7px 11px 0px #00000029",
                                    height: "6rem",
                                    pl: "60px",
                                },
                            }}
                        >
                            <Grid container direction={"row"} my={2} px={8}>
                                <Grid item xs={6} sx={flexStyles.flexRowStart}>
                                    <StyledButtonOutlined
                                        variant="outlined"
                                        sx={{
                                            visibility: "hidden",
                                            width: "220px",
                                            height: "60px",
                                        }}
                                    >
                                        Previous
                                    </StyledButtonOutlined>
                                </Grid>
                                <Grid item xs={6} sx={flexStyles.flexRowEnd} gap={2}>
                                    <Button
                                        variant="text"
                                        onClick={reset}
                                        type="button"
                                        sx={{
                                            width: "220px",
                                            color: "#FFFFFF",
                                            fontFamily: "Poppins",
                                            textTransform: "none",
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <StyledButtonContained
                                        variant="contained"
                                        type="submit"
                                        form="mainForm"
                                        sx={{
                                            width: "220px",
                                            height: "60px",
                                        }}
                                    >
                                        Save
                                    </StyledButtonContained>
                                    <StyledButtonContained
                                        variant="contained"
                                        type="submit"
                                        value="saveAndContinue"
                                        form="mainForm"
                                        sx={{
                                            visibility: isEditClient ? "hidden" : "visible",
                                            width: "220px",
                                            height: "60px",
                                            borderRadius: "30px",
                                        }}
                                    >
                                        Save & Continue
                                    </StyledButtonContained>
                                    <StyledButtonContained
                                        disabled
                                        variant="contained"
                                        type="button"
                                        sx={{
                                            visibility: isEditClient ? "hidden" : "visible",
                                            width: "220px",
                                            height: "60px",
                                            borderRadius: "30px",
                                            opacity: 0.5,
                                        }}
                                    >
                                        Skip
                                    </StyledButtonContained>
                                </Grid>
                            </Grid>
                        </Drawer>
                    )}
                </Grid>
            </Grid>
        </>
    );
};
